/**
 * <PolicyTemplateManager /> — admin list view of every onboarding template.
 *
 * Route: /admin/policy-templates
 *
 * Owner-only (firestore.rules restrict template writes to role=owner).
 * Hard-delete is denied by rules; archive sets active=false instead.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ChevronRight, FileText, Archive, RotateCcw } from 'lucide-react';
import { useToast } from '../../../components/Toast';
import {
  listAllTemplates,
  updateTemplateMeta,
} from '../services/onboardingService';
import '../Onboarding.css';

const FILTERS = [
  { id: 'active',   label: 'Active' },
  { id: 'archived', label: 'Archived' },
  { id: 'all',      label: 'All' },
];

export default function PolicyTemplateManager() {
  const navigate = useNavigate();
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active');
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const list = await listAllTemplates();
      setRows(list.sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id)));
    } catch (e) {
      toast.error(e.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (filter === 'active') return r.active !== false;
      if (filter === 'archived') return r.active === false;
      return true;
    });
  }, [rows, filter]);

  const archive = async (row) => {
    if (!window.confirm(`Archive "${row.name || row.id}"? It will no longer appear when assigning onboarding.`)) return;
    setBusyId(row.id);
    try {
      await updateTemplateMeta(row.id, { active: false });
      toast.success('Template archived');
      await load();
    } catch (e) {
      toast.error(e.message || 'Failed to archive');
    } finally {
      setBusyId(null);
    }
  };

  const restore = async (row) => {
    setBusyId(row.id);
    try {
      await updateTemplateMeta(row.id, { active: true });
      toast.success('Template restored');
      await load();
    } catch (e) {
      toast.error(e.message || 'Failed to restore');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontFamily: 'Fraunces, Georgia, serif', margin: 0 }}>Policy templates</h1>
          <p style={{ color: '#555', margin: '6px 0 0', fontSize: '0.85rem' }}>
            Manage the onboarding templates used when assigning paperwork. Add, edit, reorder, or
            remove policies inside each template.
          </p>
        </div>
        <button className="onboarding-btn" onClick={() => navigate('/admin/policy-templates/new')}>
          <Plus size={14} /> New template
        </button>
      </div>

      <div className="onboarding-filter-tabs">
        {FILTERS.map((f) => (
          <button key={f.id} className={filter === f.id ? 'active' : ''} onClick={() => setFilter(f.id)}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading-screen"><div className="spinner-large" /><p>Loading…</p></div>
      ) : filtered.length === 0 ? (
        <div className="onboarding-empty">
          {filter === 'active'
            ? 'No active templates yet. Create one to get started.'
            : 'Nothing here.'}
        </div>
      ) : (
        <table className="onboarding-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Id</th>
              <th>Version</th>
              <th>Items</th>
              <th>Status</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const itemCount = Array.isArray(r.items) ? r.items.length : 0;
              const isArchived = r.active === false;
              return (
                <tr key={r.id}>
                  <td onClick={() => navigate(`/admin/policy-templates/${r.id}`)} style={{ cursor: 'pointer' }}>
                    <FileText size={14} style={{ verticalAlign: '-2px', marginRight: 6, color: '#3b6e4a' }} />
                    {r.name || r.id}
                  </td>
                  <td onClick={() => navigate(`/admin/policy-templates/${r.id}`)} style={{ cursor: 'pointer', color: '#777', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {r.id}
                  </td>
                  <td onClick={() => navigate(`/admin/policy-templates/${r.id}`)} style={{ cursor: 'pointer' }}>
                    {r.version || '—'}
                  </td>
                  <td onClick={() => navigate(`/admin/policy-templates/${r.id}`)} style={{ cursor: 'pointer' }}>
                    {itemCount}
                  </td>
                  <td>
                    <span className={`onboarding-badge ${isArchived ? 'locked' : 'completed'}`}>
                      {isArchived ? 'Archived' : 'Active'}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {isArchived ? (
                      <button
                        className="onboarding-btn secondary"
                        disabled={busyId === r.id}
                        onClick={() => restore(r)}
                        title="Restore this template"
                      >
                        <RotateCcw size={14} /> Restore
                      </button>
                    ) : (
                      <>
                        <button
                          className="onboarding-btn secondary"
                          style={{ marginRight: 6 }}
                          onClick={() => navigate(`/admin/policy-templates/${r.id}`)}
                        >
                          Edit <ChevronRight size={14} />
                        </button>
                        <button
                          className="onboarding-btn secondary"
                          disabled={busyId === r.id}
                          onClick={() => archive(r)}
                          title="Archive (templates can't be hard-deleted)"
                        >
                          <Archive size={14} /> Archive
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
